import { Checkbox } from '@components/Checkbox/Checkbox';
import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import { PlaceholderGradient } from '@components/util/PlaceholderGradient';
import { useVirtualScroll } from '@lib/hooks/useVirtualScroll';
import { generateIdFromString } from '@lib/utils/colorUtils';
import { formatDurationToHHMMSS } from '@lib/utils/units';
import { Skeleton } from '@mui/material';
import { MediaStatusType } from '@stores/UploadURL/mediaStatus';
import classNames from 'classnames';
import React from 'react';
import css from './MobileSelectableMediaList.module.scss';

export interface SelectableMediaItem {
  id: string;
  title: string;
  thumbnail?: string;
  duration?: number;
  status: MediaStatusType | null;
  /** Metadata still being extracted — show a skeleton thumbnail. */
  isPending?: boolean;
}

const STATUS_CONFIG: Record<MediaStatusType, { label: string; className: string }> = {
  'in-library': { label: 'In library', className: css.badgeInLibrary },
  error: { label: 'Error', className: css.badgeError },
  warning: { label: 'Warning', className: css.badgeWarning },
};

const ROW_HEIGHT = 61; // row height including border

const MobileSelectableRow = React.memo<{
  item: SelectableMediaItem;
  selected: boolean;
  onToggle: (id: string) => void;
}>(({ item, selected, onToggle }) => {
  const isSelectable = item.status !== 'in-library' && item.status !== 'error';
  const statusInfo = item.status ? STATUS_CONFIG[item.status] : null;

  return (
    <div
      className={classNames(css.row, { [css.rowDisabled]: !isSelectable })}
      onClick={isSelectable ? () => onToggle(item.id) : undefined}
    >
      {isSelectable ? (
        <div className={css.checkboxWrapper} onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={selected} onChange={() => onToggle(item.id)} />
        </div>
      ) : (
        <div className={css.checkboxSpacer} />
      )}
      <div className={css.thumbnailWrapper}>
        {item.thumbnail ? (
          <img src={item.thumbnail} className={css.thumbnail} alt="" />
        ) : item.isPending ? (
          <Skeleton variant="rectangular" className={css.thumbnail} sx={{ height: '100%' }} />
        ) : (
          <div className={css.thumbnailPlaceholder}>
            <PlaceholderGradient id={generateIdFromString(item.id)} blur={20} />
          </div>
        )}
        {item.duration != null && item.duration > 0 && (
          <span className={css.duration}>{formatDurationToHHMMSS(item.duration)}</span>
        )}
      </div>
      <div className={css.titleWrapper}>
        <span className={css.title}>{item.title}</span>
        {statusInfo && <span className={statusInfo.className}>{statusInfo.label}</span>}
      </div>
    </div>
  );
});

MobileSelectableRow.displayName = 'MobileSelectableRow';

type MobileSelectableMediaListProps = {
  items: SelectableMediaItem[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
};

export const MobileSelectableMediaList = ({
  items,
  selectedIds,
  onToggle,
  onSelectAll,
  onDeselectAll,
}: MobileSelectableMediaListProps) => {
  const { scrollRef, visibleRange, totalHeight, topPad, bottomPad, clampedEnd } = useVirtualScroll(
    items.length,
    ROW_HEIGHT,
  );

  const selectedSet = new Set(selectedIds);
  const selectableItems = items.filter(
    (item) => item.status !== 'in-library' && item.status !== 'error',
  );
  const allSelected =
    selectableItems.length > 0 && selectableItems.every((item) => selectedSet.has(item.id));

  return (
    <div className={css.container}>
      <div className={css.selectAllRow}>
        <Checkbox
          checked={allSelected}
          indeterminate={selectedIds.length > 0 && !allSelected}
          onChange={() => (allSelected ? onDeselectAll?.() : onSelectAll?.())}
        />
        <span className={css.selectAllLabel}>
          {selectedIds.length} of {selectableItems.length} selected
        </span>
      </div>
      <GradientScrollDiv className={css.scrollArea} ref={scrollRef}>
        <div className={css.list} style={{ minHeight: totalHeight }}>
          {topPad > 0 && <div style={{ height: topPad, flexShrink: 0 }} />}
          {items.slice(visibleRange.start, clampedEnd).map((item) => (
            <MobileSelectableRow
              key={item.id}
              item={item}
              selected={selectedSet.has(item.id)}
              onToggle={onToggle}
            />
          ))}
          {bottomPad > 0 && <div style={{ height: bottomPad, flexShrink: 0 }} />}
        </div>
      </GradientScrollDiv>
    </div>
  );
};
